import { useEffect } from "react";
import { ArrowUpRight } from "lucide-react";
import { Reveal, ASSETS } from "@/lib/motion";
import Nav from "@/components/bwg/Nav";
import Footer from "@/components/bwg/Footer";
import WhatsAppFab from "@/components/bwg/WhatsAppFab";
import InteriorHero from "@/components/bwg/InteriorHero";
import Seo from "@/components/bwg/Seo";
import { orgSchema, breadcrumbSchema } from "@/lib/schema";

const PRESS = [
  { outlet: "Times of India", logo: ASSETS.toi,
    headline: "What zoning really means for land value in Indian cities",
    href: "https://timesofindia.indiatimes.com/real-estate/news/what-zoning-really-means-for-land-value-in-indian-cities/articleshow/127411919.cms" },
  { outlet: "Times of India", logo: ASSETS.toi,
    headline: "Old buildings vs new projects: which are safer and more sustainable",
    href: "https://timesofindia.indiatimes.com/real-estate/news/old-buildings-vs-new-projects-which-are-safer-and-more-sustainable/articleshow/128109458.cms" },
  { outlet: "Times of India", logo: ASSETS.toi,
    headline: "Budget 2026 signals renewed momentum for real estate, focus on Tier-1 and Tier-2 cities",
    href: "https://timesofindia.indiatimes.com/real-estate/news/budget-2026-signals-renewed-momentum-for-real-estate-and-focus-on-tier-1-and-tier-2-cities/articleshow/127834938.cms" },
  { outlet: "Times of India", logo: ASSETS.toi,
    headline: "8 important things to know before investing in land",
    href: "https://timesofindia.indiatimes.com/real-estate/news/8-important-things-to-know-before-investing-in-land/articleshow/127526649.cms" },
  { outlet: "Deccan Herald", logo: ASSETS.deccan,
    headline: "Housing sales paused in Dubai as West Asia conflict continues",
    href: "https://m.dailyhunt.in/news/india/english/deccan+herald-epaper-deccan/housing+sales+paused+in+dubai+as+west+asia+conflict+continues-newsid-n704033705" },
];

const COVERAGE = [
  { outlet: "Tribune India", headline: "The system behind every land fortune in India",
    href: "https://www.tribuneindia.com/news/advertorial-disclaimer/the-system-behind-every-land-fortune-in-india" },
  { outlet: "ANI News", headline: "The system behind every land fortune in India",
    href: "https://aninews.in/news/business/the-system-behind-every-land-fortune-in-india20260730134034/" },
  { outlet: "Silicon India", headline: "The deal behind Bhaarat Wealth Group",
    href: "https://www.siliconindia.com/news/general/the-deal-behind-bhaarat-wealth-group-nid-241704-cid-1.html" },
];

export default function Press() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <main className="bg-brand-paper overflow-x-hidden">
      <Seo
        title="Featured On | Bhaarat Wealth Group in the Press"
        description="Bhaarat Wealth Group's perspective on land, zoning, FAR and CLU, featured in the Times of India and Deccan Herald. Read the coverage."
        path="/press"
        schema={[orgSchema(), breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Featured On", path: "/press" }])]}
      />
      <Nav overHero />
      <InteriorHero img={ASSETS.registry} eyebrow="Featured On"
        title="Our perspective, in the press."
        sub="Where BWG has weighed in on land, zoning, FAR and the real drivers of value across India's markets." />

      <section className="bg-brand-offwhite py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-x-14 gap-y-8 mb-16 pb-12 border-b border-brand-ink/10">
            <img src={ASSETS.toi} alt="The Times of India" className="h-8 lg:h-10 object-contain opacity-70" />
            <img src={ASSETS.deccan} alt="Deccan Herald" className="h-10 lg:h-12 object-contain opacity-70" />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PRESS.map((p, i) => (
              <Reveal key={i} delay={(i % 3) * 0.1}>
                <a href={p.href} target="_blank" rel="noopener noreferrer nofollow" data-testid={`press-card-${i}`}
                  className="group flex flex-col justify-between h-full bg-brand-card border border-brand-ink/10 rounded-sm p-8 hover:border-brand-orange transition-colors duration-500 min-h-[220px]">
                  <div>
                    <img src={p.logo} alt={p.outlet} className="h-6 object-contain object-left mb-6 opacity-70" />
                    <h3 className="font-display font-semibold text-xl tracking-tight text-brand-ink leading-snug group-hover:text-brand-orange transition-colors">{p.headline}</h3>
                  </div>
                  <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-brand-grey group-hover:text-brand-orange transition-colors">
                    Visit the article <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </a>
              </Reveal>
            ))}
          </div>
          <p className="mt-10 text-xs text-brand-grey">Outbound links open the publisher's site. BWG does not control external content.</p>
        </div>
      </section>

      {/* In the news (third-party coverage) */}
      <section className="bg-brand-paper py-20 lg:py-28 border-t border-brand-ink/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <span className="text-brand-orange text-sm font-semibold tracking-[0.2em] uppercase">In the news</span>
            <h2 className="mt-4 font-display font-semibold text-3xl sm:text-4xl tracking-tighter text-brand-ink leading-[1.05] max-w-2xl">Third-party coverage of BWG.</h2>
          </Reveal>
          <div className="mt-12 divide-y divide-brand-ink/10 border-y border-brand-ink/10">
            {COVERAGE.map((c, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <a href={c.href} target="_blank" rel="noopener noreferrer nofollow" data-testid={`coverage-link-${i}`}
                  className="group flex items-center justify-between gap-6 py-6">
                  <div className="min-w-0">
                    <p className="text-xs uppercase tracking-[0.15em] text-brand-grey mb-1">{c.outlet}</p>
                    <h3 className="font-display font-medium text-lg sm:text-xl text-brand-ink group-hover:text-brand-orange transition-colors truncate">{c.headline}</h3>
                  </div>
                  <span className="shrink-0 inline-flex items-center gap-2 text-sm font-medium text-brand-grey group-hover:text-brand-orange transition-colors">
                    Read <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFab />
    </main>
  );
}
